import { ArrowDownLeft, ArrowUpRight, MessageSquare, StickyNote } from "lucide-react";
import { format, parseISO } from "date-fns";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis
} from "recharts";
import { StatCard } from "@/components/ui/stat-card";
import {
  CHART_AXIS_TICK,
  CHART_COLORS,
  CHART_TOOLTIP_STYLE,
  ChartCard
} from "@/components/ui/chart-card";
import { ChartEmptyState } from "@/components/ui/chart-empty-state";
import { OrgMessagesKpis } from "@/models/org-kpis";
import { formatters } from "@/utils/formatters";
import { ORIGIN_LABELS, formatRatio, formatUsd, labelOf, sentTypeLabel } from "../org-kpis.meta";
import { BreakdownBars } from "./BreakdownBars";

interface MessagesTabProps {
  messages: OrgMessagesKpis;
}

export function MessagesTab({ messages }: MessagesTabProps) {
  const total = messages.sent + messages.received;
  const daily = messages.daily.map((d) => ({ ...d, label: format(parseISO(d.day), "MMM d") }));
  const multiChannel = messages.byChannel.length > 1;

  return (
    <div className="space-y-6">
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard
          label="Sent"
          value={formatters.formatNumber(messages.sent)}
          hint="Bot, live chat, campaigns, API and notifications"
          icon={ArrowUpRight}
          tone="primary"
        />
        <StatCard
          label="Received"
          value={formatters.formatNumber(messages.received)}
          hint="Free: Meta never charges for customer messages"
          icon={ArrowDownLeft}
        />
        <StatCard
          label="Internal notes"
          value={formatters.formatNumber(messages.internal)}
          hint="Never sent to WhatsApp"
          icon={StickyNote}
        />
        <StatCard
          label="Total messages"
          value={formatters.formatNumber(total)}
          hint="Sent and received"
          icon={MessageSquare}
        />
      </div>

      <ChartCard
        title="Sent messages, by type"
        description="Templates are split by Meta category, which sets their price. Cost is for delivered or read messages."
      >
        {messages.sentByType.length === 0 ? (
          <p className="py-6 text-center text-sm text-muted-foreground">
            Nothing was sent in this period.
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b text-left text-muted-foreground">
                  <th className="py-2 font-medium">Type</th>
                  <th className="py-2 text-right font-medium">Sent</th>
                  <th className="py-2 text-right font-medium">Share</th>
                  <th className="py-2 text-right font-medium">Delivered</th>
                  <th className="py-2 text-right font-medium">Failed</th>
                  <th className="py-2 text-right font-medium">At the time</th>
                  <th className="py-2 text-right font-medium">New pricing</th>
                </tr>
              </thead>
              <tbody>
                {messages.sentByType.map((row) => {
                  const reached = row.delivered + row.read;
                  return (
                    <tr
                      key={`${row.kind}-${row.templateCategory ?? ""}`}
                      className="border-b last:border-0"
                    >
                      <td className="py-2">{sentTypeLabel(row.kind, row.templateCategory)}</td>
                      <td className="py-2 text-right tabular-nums">
                        {formatters.formatNumber(row.sent)}
                      </td>
                      <td className="py-2 text-right tabular-nums text-muted-foreground">
                        {messages.sent
                          ? formatters.formatPercentage((row.sent / messages.sent) * 100)
                          : "—"}
                      </td>
                      <td className="py-2 text-right tabular-nums">
                        {formatRatio(row.sent ? reached / row.sent : null)}
                      </td>
                      <td className="py-2 text-right tabular-nums">
                        {formatters.formatNumber(row.failed)}
                      </td>
                      <td className="py-2 text-right tabular-nums">{formatUsd(row.then)}</td>
                      <td className="py-2 text-right font-medium tabular-nums">
                        {formatUsd(row.new)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </ChartCard>

      <div className={multiChannel ? "grid gap-6 lg:grid-cols-2" : undefined}>
        <ChartCard title="Sent, by where it came from">
          <BreakdownBars
            rows={messages.byOrigin.map((o) => ({
              label: labelOf(ORIGIN_LABELS, o.origin),
              value: o.count
            }))}
            format={formatters.formatNumber}
          />
        </ChartCard>
        {multiChannel && (
          <ChartCard title="Sent, by channel">
            <BreakdownBars
              rows={messages.byChannel.map((c) => ({
                label: `${c.name} (${c.type.toLowerCase()})`,
                value: c.sent
              }))}
              format={formatters.formatNumber}
            />
          </ChartCard>
        )}
      </div>

      <ChartCard title="Messages per day" description="Sent and received, by UTC day">
        {daily.length === 0 ? (
          <ChartEmptyState
            icon={MessageSquare}
            title="No messages in this period"
            description="Pick a longer period, or check that the usage rollup has run."
          />
        ) : (
          <div className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={daily} margin={{ left: 0, right: 8, top: 8 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
                <XAxis dataKey="label" tick={CHART_AXIS_TICK} tickLine={false} minTickGap={16} />
                <YAxis tick={CHART_AXIS_TICK} tickLine={false} axisLine={false} width={48} />
                <Tooltip
                  contentStyle={CHART_TOOLTIP_STYLE}
                  cursor={{ fill: "hsl(var(--muted))" }}
                />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Bar dataKey="sent" name="Sent" stackId="a" fill={CHART_COLORS[0]} />
                <Bar
                  dataKey="received"
                  name="Received"
                  stackId="a"
                  fill={CHART_COLORS[1]}
                  radius={[3, 3, 0, 0]}
                />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
      </ChartCard>
    </div>
  );
}
